import React, { useEffect, lazy, Suspense, useMemo, useRef } from "react";
import "./App.css";
import { MainRoutes } from "./routes/all";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ThemeProvider } from "@mui/material/styles";
import { useStore } from "./helpers";
import { FTPTheme } from "./theme";

// Lazy load React Query Devtools only in development
const ReactQueryDevtools =
    process.env.NODE_ENV === "development"
        ? lazy(() =>
              import("@tanstack/react-query-devtools").then((mod) => ({
                  default: mod.ReactQueryDevtools,
              }))
          )
        : ((() => null) as React.ComponentType<any>);

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            // Fix: Always execute queries regardless of navigator.onLine status
            // Chrome sometimes incorrectly reports offline when the network is available
            networkMode: "always",
        },
        mutations: {
            // Fix: Always execute mutations regardless of navigator.onLine status
            // Chrome sometimes incorrectly reports offline when the network is available
            networkMode: "always",
        },
    },
});

function App() {
    const rootStore = useStore();

    // Create theme in component to avoid module-level initialization issues
    const theme = useMemo(() => FTPTheme, []);
    const secretsInitialized = useRef(false);

    useEffect(() => {
        // Ensure secrets are only initialized once, even if component remounts
        if (secretsInitialized.current) {
            return;
        }
        const init_secret = async () => {
            try {
                await rootStore.init_cloud_secrets();
                secretsInitialized.current = true;
            } catch (error) {
                console.error("Error during init_secret:", error);
            }
        };
        init_secret();
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <div
                className="App"
                style={{ width: "100%", margin: 0, padding: 0 }}
            >
                <QueryClientProvider client={queryClient}>
                    <MainRoutes />
                    <ToastContainer
                        position="top-right"
                        autoClose={3000}
                        hideProgressBar={false}
                        newestOnTop={true}
                        closeOnClick
                        rtl={false}
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                        theme="light"
                        style={{
                            fontSize: "14px",
                        }}
                    />
                    {process.env.NODE_ENV === "development" && (
                        <Suspense fallback={null}>
                            <ReactQueryDevtools
                                initialIsOpen={false}
                                position={"bottom-right"}
                            />
                        </Suspense>
                    )}
                </QueryClientProvider>
            </div>
        </ThemeProvider>
    );
}

export default App;
